import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, ArrowRight, Sparkles, Volume2, RotateCcw, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SpiralCinematic } from "@/components/SpiralCinematic";
import { pickSparks } from "@/lib/onboardingSparks";
import { useAuth } from "@/context/AuthContext";

const L = {
  fr: { pick: "Ou choisis une étincelle", lighting: "L'Élan illumine ta première étincelle…", listening: "L'Élan te lit ton étincelle…", replay: "Réécouter", continue: "Continuer avec L'Élan", go: "Faire naître mon étincelle" },
  en: { pick: "Or pick a spark", lighting: "L'Élan is lighting your first spark…", listening: "L'Élan is reading your spark…", replay: "Listen again", continue: "Continue with L'Élan", go: "Spark my first wonder" },
  es: { pick: "O elige una chispa", lighting: "L'Élan enciende tu primera chispa…", listening: "L'Élan te lee tu chispa…", replay: "Escuchar de nuevo", continue: "Continuar con L'Élan", go: "Encender mi chispa" },
  nl: { pick: "Of kies een vonk", lighting: "L'Élan ontsteekt je eerste vonk…", listening: "L'Élan leest je vonk voor…", replay: "Opnieuw luisteren", continue: "Doorgaan met L'Élan", go: "Mijn eerste vonk aansteken" },
};

export default function Onboarding({ onDone }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const ritualEnabled = !!(user && user.ritual_enabled);
  const lang = (i18n.language || "fr").slice(0, 2);
  const l = L[lang] || L.fr;
  const sparks = useMemo(() => pickSparks(lang), [lang]);

  const [phase, setPhase] = useState("choose"); // choose | reveal
  const [answer, setAnswer] = useState("");
  const [ritual, setRitual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [spark, setSpark] = useState(null); // { question, answer, image, audio, caption }
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const topicRef = useRef("");

  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  const persistPrefs = async () => {
    if (ritual && ritualEnabled) await api.put("/preferences", { morning_ritual: true }).catch(() => {});
    await api.post("/onboarding/complete").catch(() => {});
  };

  const playAudio = (b64) => {
    if (!b64) return;
    try {
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(`data:audio/mpeg;base64,${b64}`);
      audioRef.current = a;
      a.onended = () => setPlaying(false);
      a.onplay = () => setPlaying(true);
      a.onpause = () => setPlaying(false);
      a.play().catch(() => setPlaying(false));
    } catch { setPlaying(false); }
  };

  const igniteSpark = async (topic) => {
    const clean = (topic || "").trim();
    if (!clean || busy) return;
    topicRef.current = clean;
    setBusy(true);
    setPhase("reveal");
    try {
      const { data } = await api.post("/onboarding/spark", { topic: clean, lang });
      setSpark(data);
      persistPrefs();
      if (data.audio) setTimeout(() => playAudio(data.audio), 350);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      // Fallback : on entre quand même dans Le Fil avec le sujet choisi.
      await persistPrefs();
      onDone(clean);
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (busy) return;
    setBusy(true);
    try { await persistPrefs(); } finally { onDone(null); }
  };

  const continueToFil = async () => {
    if (audioRef.current) audioRef.current.pause();
    onDone(topicRef.current || null);
  };

  return (
    <motion.div
      data-testid="onboarding-overlay"
      className="fixed inset-0 z-[80] starfield flex items-center justify-center p-6 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <AnimatePresence mode="wait">
        {phase === "choose" ? (
          <motion.div
            key="choose"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6 }}
            className="w-full max-w-lg text-center my-auto"
          >
            <div className="flex justify-center mb-2"><SpiralCinematic size={130} particles={280} /></div>
            <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-3">{t("onboarding.title")}</p>
            <p className="text-foreground/60 leading-relaxed mb-7">{t("onboarding.subtitle")}</p>
            <h1 className="font-display text-gradient-orange italic text-3xl sm:text-4xl leading-tight mb-6">
              {t("onboarding.question")}
            </h1>
            <textarea
              data-testid="onboarding-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={2}
              placeholder={t("onboarding.placeholder")}
              className="w-full glass frame-pulse rounded-2xl p-4 text-lg text-foreground placeholder:text-foreground/30 outline-none resize-none text-center"
            />

            <p className="text-foreground/40 text-xs uppercase tracking-[0.2em] mt-6 mb-3">{l.pick}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {sparks.map((s, i) => (
                <button
                  key={i}
                  data-testid={`onboarding-spark-chip-${i}`}
                  onClick={() => igniteSpark(s)}
                  disabled={busy}
                  className="text-sm px-4 py-2 rounded-full border border-primary/25 bg-primary/[0.06] text-foreground/75 hover:text-primary hover:border-primary/50 hover:bg-primary/10 transition-colors disabled:opacity-40"
                >
                  {s}
                </button>
              ))}
            </div>

            {ritualEnabled && (
              <label className="flex items-center justify-center gap-3 mt-7 text-sm text-foreground/60 cursor-pointer">
                <input type="checkbox" data-testid="onboarding-ritual" checked={ritual} onChange={(e) => setRitual(e.target.checked)} className="accent-[hsl(35,80%,60%)] w-4 h-4" />
                <Mail size={15} className="text-primary/70" />
                {t("onboarding.ritual")}
              </label>
            )}

            <div className="flex flex-col items-center gap-4 mt-8">
              <button
                data-testid="onboarding-cta"
                onClick={() => igniteSpark(answer)}
                disabled={busy || !answer.trim()}
                className="btn-frost-orange inline-flex items-center gap-3 px-9 py-4 rounded-full font-semibold text-lg disabled:opacity-40"
              >
                <Sparkles size={18} /> {l.go}
              </button>
              <button
                data-testid="onboarding-skip"
                onClick={skip}
                disabled={busy}
                className="text-sm text-foreground/40 hover:text-primary transition-colors"
              >
                {t("onboarding.skip")}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
            className="w-full max-w-xl text-center my-auto"
            data-testid="onboarding-reveal"
          >
            {busy && !spark ? (
              <div className="flex flex-col items-center gap-5 py-10" data-testid="onboarding-loading">
                <SpiralCinematic size={150} particles={320} />
                <p className="font-display italic text-xl text-primary/80">{l.lighting}</p>
              </div>
            ) : spark ? (
              <>
                <div className="relative rounded-[1.75rem] overflow-hidden border border-primary/25 frame-pulse mb-6 mx-auto max-w-md aspect-square bg-black/40">
                  {spark.image ? (
                    <motion.img
                      data-testid="onboarding-spark-image"
                      src={`data:${spark.mime || "image/png"};base64,${spark.image}`}
                      alt={spark.caption || ""}
                      initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1 }}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><SpiralCinematic size={160} particles={300} /></div>
                  )}
                  {spark.caption && (
                    <div className="absolute bottom-0 inset-x-0 p-3 text-xs text-foreground/70 bg-gradient-to-t from-black/80 to-transparent">{spark.caption}</div>
                  )}
                </div>

                <h2 className="font-display text-gradient-orange italic text-2xl sm:text-3xl leading-tight mb-4" data-testid="onboarding-spark-question">{spark.question}</h2>
                <p className="font-display italic text-foreground/90 text-lg leading-[1.8] mb-6" data-testid="onboarding-spark-answer">{spark.answer}</p>

                <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                  {spark.audio && (
                    <div className="inline-flex items-center gap-2 text-sm text-primary/80" data-testid="onboarding-audio-state">
                      {playing ? <Volume2 size={16} className="animate-pulse" /> : <Volume2 size={16} />}
                      {playing && <span>{l.listening}</span>}
                      <button
                        data-testid="onboarding-replay"
                        onClick={() => playAudio(spark.audio)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/25 hover:border-primary/50 hover:bg-primary/10 transition-colors"
                      >
                        <RotateCcw size={14} /> {l.replay}
                      </button>
                    </div>
                  )}
                </div>

                <button
                  data-testid="onboarding-continue"
                  onClick={continueToFil}
                  className="btn-frost-orange inline-flex items-center gap-3 px-9 py-4 rounded-full font-semibold text-lg"
                >
                  {l.continue} <ArrowRight size={18} />
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center gap-4 py-10">
                <Loader2 size={28} className="animate-spin text-primary/70" />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
